import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Briefcase,
  FileText,
  Search as SearchIcon,
  LogOut,
  ArrowLeft,
  Users,
  Menu,
  ScrollText,
  Wallet,
  Tags,
  Mail,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAdminSearch } from '@/hooks/admin/useAdminSearch';
import { HiroMatchLogo } from '@/components/branding/HiroMatchLogo';
import { AdminCopilotPanel } from '@/components/admin/AdminCopilotPanel';

/**
 * AdminLayout — outer chrome of the Backoffice.
 *
 * Visually distinct from the recruiter-facing `AppLayout`:
 *   - Dedicated sidebar with the 5 admin sections.
 *   - Header carries a red "Admin" badge so an operator can never
 *     confuse the panels at a glance.
 *   - Header search bar (D1.3) wired to `/admin/search` — clicking a
 *     suggestion deep-links into the relevant customer detail.
 *   - "Exit admin" link returns to the regular `/dashboard`.
 *
 * The shell is mounted under a `<SudoGuard>` parent route so any
 * unauthorized access bounces back before this component renders.
 */
const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/customers', label: 'Customers', icon: Building2 },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { to: '/admin/finance', label: 'Finance', icon: Wallet },
  { to: '/admin/plans', label: 'Plans', icon: Tags },
  { to: '/admin/postings', label: 'Postings', icon: Briefcase },
  { to: '/admin/applications', label: 'Applications', icon: FileText },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
  { to: '/admin/email-audit', label: 'Email Audit', icon: Mail },
  { to: '/admin/compliance', label: 'Compliance', icon: ShieldCheck },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);

  // Close the mobile drawer whenever the user navigates — otherwise
  // tapping a NavLink lands on the new route with the drawer still
  // covering the screen.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Cmd/Ctrl + / toggle Copilot panel (sprint admin_ai_copilot S5)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setCopilotOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const sidebar = (
    <>
      <div className="px-4 py-4 border-b border-sidebar-border">
        <Link to="/admin" className="flex items-center gap-2.5 group">
          <HiroMatchLogo className="h-8 w-8" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-sidebar-foreground">HiroMatch</p>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-destructive bg-destructive/10 rounded px-1.5 py-0.5">
              Admin
            </span>
          </div>
        </Link>
      </div>

      <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border mx-2" />

      <div className="p-2 space-y-1">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
        >
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" /> Exit admin
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
          onClick={logout}
        >
          <LogOut className="h-4 w-4" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-muted/20">
      <Helmet>
        <title>HiroMatch</title>
      </Helmet>
      {/* Desktop sidebar (>= lg / 1024px) */}
      <aside className="w-60 bg-sidebar border-r border-sidebar-border flex-col hidden lg:flex">
        {sidebar}
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header — mobile hamburger appears on < lg, desktop hides it */}
        <header className="h-14 bg-card border-b border-border flex items-center px-3 sm:px-4 gap-2 sm:gap-3">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-9 w-9"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-sidebar border-sidebar-border flex flex-col">
              {sidebar}
            </SheetContent>
          </Sheet>
          <AdminSearchBar />
          <div className="flex-1" />

          {/* AI Co-pilot toggle — sprint admin_ai_copilot S5 (28/05/26) */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCopilotOpen(true)}
            className="h-8 gap-1.5 text-xs"
            title="Apri AI Co-pilot (Cmd+/)"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="hidden sm:inline">Co-pilot</span>
            <kbd className="hidden md:inline text-[9px] font-mono text-muted-foreground border border-border rounded px-1 py-0">⌘/</kbd>
          </Button>

          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md bg-destructive/10 text-destructive font-semibold uppercase tracking-wide whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
            <span className="hidden sm:inline">Admin mode</span>
            <span className="sm:hidden">Admin</span>
          </span>
          <span className="text-xs text-muted-foreground hidden md:inline">{user?.email}</span>
        </header>

        {/* Route content */}
        <main key={location.pathname} className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* AI Co-pilot floating panel */}
      <AdminCopilotPanel open={copilotOpen} onOpenChange={setCopilotOpen} />
    </div>
  );
}

/**
 * Compact admin search bar in the header. Debounced (300ms) calls to
 * `/admin/search?type=customer`. Click on a suggestion navigates to
 * the customer detail page. D1.3 = exact-match in-memory.
 */
function AdminSearchBar() {
  const [query, setQuery] = useState('');
  const { results, isLoading } = useAdminSearch(query);
  const showDropdown = query.length >= 2 && results.customers.length > 0;
  return (
    <div className="relative w-full max-w-md">
      <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Cerca per email o nome company..."
        className="h-8 pl-8 text-sm"
      />
      {isLoading && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      )}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border border-border bg-popover shadow-md overflow-hidden max-h-64 overflow-y-auto">
          {results.customers.map((c) => (
            <Link
              key={c.companyId}
              to={`/admin/customers/${c.companyId}`}
              onClick={() => setQuery('')}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="truncate font-medium">{c.publicName || c.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {c.segment ?? 'sme'} · {c.planId ?? 'no plan'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
